/**
 * How a pour stops monitoring (PourInfo.StopType, legacy codes), shared by
 * the create and edit pages.
 *
 * - '31': stop XHours DAYS after the pour started.
 * - '7': stop XHours HOURS after every maturity location has reached its
 *   final (highest) maturity target. Only a pour that computes maturity
 *   has targets to reach.
 * - '22': stop at PreSetStopTime (stored as naive UTC).
 *
 * Other legacy codes are kept on the pours that have them, but never offered.
 */
import { parseUtc } from './datetime';

export const STOP_AFTER_DAYS = '31';
export const STOP_AFTER_FINAL_TARGETS = '7';
export const STOP_AT_SET_TIME = '22';

export const STOP_TYPE_LABELS: Record<string, string> = {
  [STOP_AFTER_DAYS]: 'Stop after X days',
  [STOP_AFTER_FINAL_TARGETS]: 'Stop X hours after all final targets are reached',
  [STOP_AT_SET_TIME]: 'Stop at a set time',
};

/** The day counts offered as one-click chips. */
export const QUICK_STOP_DAYS = [7, 14, 28, 56];

/** Whether the code is one this app offers. */
export const isKnownStopType = (stopType: string): boolean =>
  Object.prototype.hasOwnProperty.call(STOP_TYPE_LABELS, stopType);

/** The label for a stop type: its STOP_TYPE_LABELS entry, "Legacy stop
 *  type N" for a code this app does not offer, or '' when none is set. */
export const stopTypeLabel = (stopType: string): string => {
  if (isKnownStopType(stopType)) return STOP_TYPE_LABELS[stopType];
  return stopType ? `Legacy stop type ${stopType}` : '';
};

/** What XHours counts for a stop type, or null when it is not used. */
export const xHoursUnit = (stopType: string): 'days' | 'hours' | null => {
  if (stopType === STOP_AFTER_DAYS) return 'days';
  if (stopType === STOP_AFTER_FINAL_TARGETS) return 'hours';
  return null;
};

/** XHours a newly picked stop type starts with: 28 days, or 24 hours. */
export const defaultXHours = (stopType: string): string =>
  stopType === STOP_AFTER_FINAL_TARGETS ? '24' : '28';

/** A whole number of days or hours, or null when the text is not one. */
export const parseXHours = (value: string | null | undefined): number | null => {
  const text = (value ?? '').trim();
  return /^\d+$/.test(text) ? Number(text) : null;
};

export interface StopSettings {
  stopType: string;
  xHours: string;
  /** PreSetStopTime as naive UTC; '' when not set. */
  presetUtc: string;
  computesMaturity: boolean;
  /** The pour will have no maturity targets (create page: no maturity
   *  target group picked), so a target-based stop is refused as the server
   *  refuses it. */
  noTargets?: boolean;
  /** The set time must still be ahead (a new or changed time). */
  presetMustBeFuture?: boolean;
  /** The pour start as naive UTC: a set time must be later. */
  startUtc?: string;
  /** Unknown codes are refused (a new pour) or left alone (a saved one). */
  allowLegacyStopType?: boolean;
  now?: Date;
}

/** Field errors for the stop settings, keyed stopType, xHours and
 *  presetStopTime. Empty when the settings can be saved. */
export const stopSettingsErrors = (s: StopSettings): Record<string, string> => {
  const errs: Record<string, string> = {};
  if (s.stopType === STOP_AFTER_DAYS) {
    const days = parseXHours(s.xHours);
    if (days === null || days < 1) errs.xHours = 'Enter a whole number of days (1 or more).';
  } else if (s.stopType === STOP_AFTER_FINAL_TARGETS) {
    if (!s.computesMaturity) {
      errs.stopType =
        'Stopping after the final targets needs Concrete Maturity. Pick a number of days instead.';
    } else if (s.noTargets) {
      errs.stopType =
        'Stopping after the final targets needs a maturity target group. Pick one, or pick a number of days instead.';
    }
    if (parseXHours(s.xHours) === null) errs.xHours = 'Enter a whole number of hours (0 or more).';
  } else if (s.stopType === STOP_AT_SET_TIME) {
    const at = parseUtc(s.presetUtc).getTime();
    if (!s.presetUtc || isNaN(at)) {
      errs.presetStopTime = 'Pick the time to stop monitoring.';
    } else if (s.presetMustBeFuture && at <= (s.now ?? new Date()).getTime()) {
      errs.presetStopTime = 'This time has passed. Pick a time in the future.';
    } else if (s.startUtc && at <= parseUtc(s.startUtc).getTime()) {
      errs.presetStopTime = 'The stop time must be after the pour start time.';
    }
  } else if (!s.allowLegacyStopType) {
    errs.stopType = 'Choose how monitoring stops.';
  }
  return errs;
};

/** One line for a review or summary, e.g. "After 28 days". `presetText` is
 *  the set time as shown to the user (with its timezone). */
export const describeStop = (stopType: string, xHours: string, presetText: string): string => {
  const n = xHours.trim() || '0';
  if (stopType === STOP_AFTER_DAYS) return `After ${n} day${n === '1' ? '' : 's'}`;
  if (stopType === STOP_AFTER_FINAL_TARGETS) {
    return `${n} hour${n === '1' ? '' : 's'} after all final targets are reached`;
  }
  if (stopType === STOP_AT_SET_TIME) return presetText ? `At ${presetText}` : 'At a set time (not set)';
  return stopType ? `Legacy stop type ${stopType}` : 'Not set';
};
